import { useState, type ChangeEvent } from 'react';
import {
  parseNutrition,
  getMealieRecipes,
  saveMeal,
  type MealItemDraft,
  type MealieRecipe,
} from '../api.js';
import {
  emptyItem,
  parseNumInput,
  updateItem,
  removeItem,
  itemTotals,
  savableItems,
  buildMealPayload,
  type NumericField,
} from '../lib/draftItems.js';
import { fmtNumber } from '../lib/format.js';
import BarcodeScanner from './BarcodeScanner.js';
import { Button, Icon, Segmented, Sheet } from '../ui/index.js';

type Tab = 'scan' | 'photo' | 'describe' | 'mealie';

const TABS: { id: Tab; label: string }[] = [
  { id: 'scan', label: 'Scan' },
  { id: 'photo', label: 'Photo' },
  { id: 'describe', label: 'Describe' },
  { id: 'mealie', label: 'Recipe' },
];

const COLUMNS: { field: NumericField; label: string }[] = [
  { field: 'qty', label: 'Qty' },
  { field: 'calories', label: 'kcal' },
  { field: 'protein_g', label: 'P' },
  { field: 'carbs_g', label: 'C' },
  { field: 'fat_g', label: 'F' },
  { field: 'caffeine_mg', label: 'Caf' },
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      // Strip the "data:<mime>;base64," prefix.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface MealEntryProps {
  onSaved: () => void;
  onCancel: () => void;
  initialTab?: Tab;
}

export default function MealEntry({ onSaved, onCancel, initialTab }: MealEntryProps): JSX.Element {
  const [tab, setTab] = useState<Tab>(initialTab ?? 'describe');
  const [items, setItems] = useState<MealItemDraft[]>([]);
  const [mealName, setMealName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab-local inputs.
  const [scanning, setScanning] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [describe, setDescribe] = useState('');
  const [recipeQuery, setRecipeQuery] = useState('');
  const [recipes, setRecipes] = useState<MealieRecipe[]>([]);
  const [mealieDisabled, setMealieDisabled] = useState(false);
  const [servings, setServings] = useState('1');

  const totals = itemTotals(items);
  const canSave = savableItems(items).length > 0 && !busy;

  function append(newItems: MealItemDraft[]): void {
    if (newItems.length === 0) {
      setError('No items found. Add them manually below.');
      return;
    }
    setItems((prev) => [...prev, ...newItems]);
  }

  async function run(fn: () => Promise<void>): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  function onBarcode(code: string): void {
    setScanning(false);
    setBarcode(code);
    void run(async () => {
      const res = await parseNutrition({ barcode: code });
      if (!res.found) {
        setError(`No product found for ${code}. Add it manually below.`);
        return;
      }
      append(res.items);
    });
  }

  function onPhoto(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void run(async () => {
      const imageBase64 = await fileToBase64(file);
      const res = await parseNutrition({ imageBase64, imageMediaType: file.type || 'image/jpeg' });
      append(res.items);
    });
  }

  function onDescribe(): void {
    if (describe.trim().length === 0) return;
    void run(async () => {
      const res = await parseNutrition({ text: describe.trim() });
      append(res.items);
      setDescribe('');
    });
  }

  function onRecipeSearch(): void {
    if (recipeQuery.trim().length === 0) return;
    void run(async () => {
      const res = await getMealieRecipes(recipeQuery.trim());
      setMealieDisabled(res.enabled === false);
      setRecipes(res.recipes);
    });
  }

  function onPickRecipe(recipe: MealieRecipe): void {
    void run(async () => {
      const res = await parseNutrition({
        mealieRecipe: { slug: recipe.slug, servings: parseNumInput(servings) ?? 1 },
      });
      if (res.enabled === false) {
        setError('Mealie is not configured on the server.');
        return;
      }
      append(res.items);
    });
  }

  function editNumeric(index: number, field: NumericField, value: string): void {
    setItems((prev) => updateItem(prev, index, { [field]: parseNumInput(value) }));
  }

  function save(): void {
    void run(async () => {
      await saveMeal(buildMealPayload({ name: mealName, items }));
      onSaved();
    });
  }

  return (
    <Sheet
      title="Add meal"
      onClose={busy ? () => undefined : onCancel}
      action={{ label: busy ? 'Saving…' : 'Save', onClick: save, disabled: !canSave }}
      className="me-sheet"
    >
      <div className="mealentry">
        <div className="hb-seg">
          <Segmented label="Add from" value={tab} options={TABS.map((t) => ({ value: t.id, label: t.label }))} onChange={setTab} />
        </div>

        <div className="me-source">
          {tab === 'scan' &&
            (scanning ? (
              <BarcodeScanner onDetected={onBarcode} onClose={() => setScanning(false)} />
            ) : (
              <div className="hb-form">
                <Button variant="primary" icon="target" block onClick={() => setScanning(true)} disabled={busy}>
                  Open camera
                </Button>
                <div className="me-inline">
                  <input
                    placeholder="or type a barcode"
                    aria-label="Barcode"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && barcode.trim() && onBarcode(barcode.trim())}
                    inputMode="numeric"
                  />
                  <Button variant="secondary" onClick={() => onBarcode(barcode.trim())} disabled={busy || !barcode.trim()}>
                    Look up
                  </Button>
                </div>
              </div>
            ))}

          {tab === 'photo' && (
            <div className="hb-form">
              <label className="ui-btn ui-btn--primary ui-btn--md ui-btn--block">
                Choose photo
                <input type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
              </label>
              <p className="hb-hint">A photo of your plate is estimated by AI.</p>
            </div>
          )}

          {tab === 'describe' && (
            <div className="hb-form">
              <textarea
                className="me-textarea"
                data-autofocus
                aria-label="Describe the meal"
                placeholder="e.g. grilled chicken breast, a cup of rice, and a black coffee"
                value={describe}
                onChange={(e) => setDescribe(e.target.value)}
                rows={3}
              />
              <Button variant="primary" block onClick={onDescribe} disabled={busy || !describe.trim()}>
                {busy ? 'Estimating…' : 'Estimate'}
              </Button>
            </div>
          )}

          {tab === 'mealie' && (
            <div className="hb-form">
              <div className="me-inline">
                <input
                  placeholder="Search your recipes"
                  aria-label="Search your recipes"
                  value={recipeQuery}
                  onChange={(e) => setRecipeQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onRecipeSearch()}
                />
                <Button variant="secondary" onClick={onRecipeSearch} disabled={busy}>
                  Search
                </Button>
              </div>
              <label className="me-servings">
                <span>Servings</span>
                <input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                />
              </label>
              {mealieDisabled && <p className="hb-hint">Mealie is not configured on the server.</p>}
              {recipes.length > 0 && (
                <ul className="ui-group me-recipes">
                  {recipes.map((r) => (
                    <li key={r.slug} className="ui-swipe">
                      <button type="button" className="ui-pick" onClick={() => onPickRecipe(r)} disabled={busy}>
                        <span className="ui-pick__label">{r.name}</span>
                        <Icon name="plus" size={18} className="ui-pick__check" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="hb-form__error" role="alert">
            {error}
          </p>
        )}

        <label className="hb-field me-name">
          <span>Meal name</span>
          <input placeholder="Optional" value={mealName} onChange={(e) => setMealName(e.target.value)} />
        </label>

        <div className="me-items">
          <div className="me-items__head">
            <span className="hb-field__label">Items</span>
            <span className="me-items__totals num">
              {fmtNumber(totals.calories, 0)} kcal · {fmtNumber(totals.protein_g, 0)}P · {fmtNumber(totals.carbs_g, 0)}C ·{' '}
              {fmtNumber(totals.fat_g, 0)}F
              {totals.caffeine_mg > 0 ? ` · ${fmtNumber(totals.caffeine_mg, 0)} mg caf` : ''}
            </span>
          </div>

          {items.length === 0 ? (
            <p className="me-empty">Items you find above land here to check before saving.</p>
          ) : (
            items.map((it, i) => (
              <div className="me-item" key={i}>
                <div className="me-item__top">
                  <input
                    className="me-item__name"
                    placeholder="Name"
                    aria-label={`Item ${i + 1} name`}
                    value={it.name}
                    onChange={(e) => setItems((prev) => updateItem(prev, i, { name: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="me-item__del"
                    aria-label="Remove item"
                    onClick={() => setItems((prev) => removeItem(prev, i))}
                  >
                    <Icon name="x" size={18} />
                  </button>
                </div>
                <div className="me-item__nums">
                  {COLUMNS.map((c) => (
                    <label key={c.field} className="me-num">
                      <span>{c.label}</span>
                      <input
                        inputMode="decimal"
                        aria-label={`Item ${i + 1} ${c.label}`}
                        value={it[c.field] ?? ''}
                        onChange={(e) => editNumeric(i, c.field, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}

          <button type="button" className="me-addrow" onClick={() => setItems((prev) => [...prev, emptyItem()])}>
            <Icon name="plus" size={18} />
            Add item by hand
          </button>
        </div>

      </div>
    </Sheet>
  );
}
